// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import { ContextMenuTrigger, ContextMenu, MenuItem } from 'react-contextmenu';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Icon from 'shared/components/common/Icon';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import AddEditVendorModal from 'main-app/components/AddEditVendorModal';
import DeleteVendorMutation from 'main-app/mutations/DeleteVendor';
import * as Actions from 'main-app/store/Actions';

type Props = {
  history: RouterHistory,
};

type State = {
  addModalOpen: boolean,
  updateModalRecord: ?Object,
  searchQuery: string,
};

const ListWrapper = styled.div`
  flex: 1;
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: #666;
  font-size: 16px;
`;

const StarRating = styled.div`
  color: #ffa500;
  font-size: 16px;
`;

class VendorList extends PureComponent<Props, State> {
  menuTriggerRefs = {};

  constructor(props: Props) {
    super(props);

    this.state = {
      addModalOpen: false,
      updateModalRecord: null,
      searchQuery: '',
    };
  }

  handleOpenUpdateModal = (target: *, vendorEdges: ?Array<Object>) => {
    const vendorId = target.getAttribute('vendor-id');

    const vendor = vendorEdges
      ? vendorEdges.find(({ node }) => node.id === vendorId)
      : null;

    if (vendor) {
      this.setState({
        updateModalRecord: vendor.node,
      });
    }
  };

  handleOpenCreateModal = () => {
    this.setState({
      addModalOpen: true,
    });
  };

  handleCloseAllModals = () => {
    this.setState({
      addModalOpen: false,
      updateModalRecord: null,
    });
  };

  handleDeleteVendor = async (target: *, vendorEdges: ?Array<Object>) => {
    const vendorId = target.getAttribute('vendor-id');

    const vendor = vendorEdges
      ? vendorEdges.find(({ node }) => node.id === vendorId)
      : null;

    if (!vendor) return;

    const confirmed = window.confirm(
      i18n.t(`Are you sure you want to delete vendor "${vendor.node.name}"?`)
    );

    if (!confirmed) return;

    try {
      await DeleteVendorMutation.commit({
        variables: {
          input: {
            id: vendorId,
          },
        },
      });

      Actions.alertNotification(
        i18n.t('Vendor Successfully Deleted'),
        'success'
      );
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    }
  };

  handleSearchChange = (searchQuery: string) => {
    this.setState({ searchQuery });
  };

  filterVendors = (vendors: Array<Object>) => {
    const { searchQuery } = this.state;
    if (!searchQuery.trim()) return vendors;

    const query = searchQuery.toLowerCase();
    return vendors.filter(({ node }) => {
      return (
        node.vendorNumber?.toLowerCase().includes(query) ||
        node.name?.toLowerCase().includes(query) ||
        node.email?.toLowerCase().includes(query)
      );
    });
  };

  renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  render() {
    const { history } = this.props;
    const { addModalOpen, updateModalRecord, searchQuery } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        dataFrom="STORE_THEN_NETWORK"
        query={graphql`
          query VendorListQuery {
            vendors(first: 1000)
              @connection(key: "VendorList_vendors", filters: []) {
              edges {
                node {
                  id
                  vendorNumber
                  name
                  email
                  phone
                  address
                  city
                  state
                  zipCode
                  country
                  paymentTerms
                  rating
                  createdAt
                  updatedAt
                  ...AddEditVendorModal_vendor
                }
              }
            }
          }
        `}
        variables={null}
        render={({ props }) => {
          const filteredVendors = props
            ? this.filterVendors(props.vendors.edges)
            : [];

          return (
            <Page>
              <PageHeaderList
                title={i18n.t('Vendors')}
                createButtonLabel={props && i18n.t('Add Vendor')}
                onCreateButtonClick={this.handleOpenCreateModal}
                searchPlaceholder={i18n.t('Search by vendor number, name, or email...')}
                searchValue={searchQuery}
                onSearchChange={this.handleSearchChange}
              />

              {!props ? (
                <Loader />
              ) : !filteredVendors.length ? (
                <EmptyState>
                  {searchQuery.trim()
                    ? i18n.t('No vendors found matching your search.')
                    : i18n.t("You haven't added any vendors yet.")}
                </EmptyState>
              ) : (
                <ListWrapper>
                  <Table
                    data={filteredVendors.map(edge => edge.node)}
                    columns={[
                      {
                        Header: i18n.t('Vendor Number'),
                        accessor: 'vendorNumber',
                        width: 120,
                        Cell: ({ value }) => (
                          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {value || '-'}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('Name'),
                        accessor: 'name',
                        width: 180,
                        Cell: ({ value }) => (
                          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {value}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('Email'),
                        accessor: 'email',
                        width: 200,
                        Cell: ({ value }) => (
                          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {value || '-'}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('Phone'),
                        accessor: 'phone',
                        width: 140,
                        Cell: ({ value }) => (
                          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {value || '-'}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('City'),
                        accessor: 'city',
                        width: 120,
                        Cell: ({ value }) => (
                          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {value || '-'}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('State'),
                        accessor: 'state',
                        width: 80,
                        Cell: ({ value }) => (
                          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {value || '-'}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('Rating'),
                        accessor: 'rating',
                        width: 120,
                        Cell: ({ value }) => (
                          <StarRating style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {this.renderStars(value || 5)}
                          </StarRating>
                        ),
                      },
                      {
                        Header: '',
                        accessor: 'id',
                        width: 48,
                        enforceWidth: true,
                        Cell: ({ value }) => (
                          <ContextMenuTrigger
                            ref={r => (this.menuTriggerRefs[value] = r)}
                            id="vendor-list-menu"
                            attributes={{
                              'vendor-id': value,
                            }}
                          >
                            <Icon
                              type="circle-context-menu"
                              size={24}
                              onClick={e => {
                                if (this.menuTriggerRefs[value]) {
                                  this.menuTriggerRefs[
                                    value
                                  ].handleContextClick(e);
                                }
                              }}
                            />
                          </ContextMenuTrigger>
                        ),
                      },
                    ]}
                  />
                </ListWrapper>
              )}

              {addModalOpen && (
                <AddEditVendorModal
                  history={history}
                  onClose={this.handleCloseAllModals}
                  vendor={null}
                />
              )}
              {updateModalRecord && (
                <AddEditVendorModal
                  history={history}
                  onClose={this.handleCloseAllModals}
                  vendor={updateModalRecord}
                />
              )}

              <ContextMenu id="vendor-list-menu">
                <MenuItem
                  onClick={(e, data, target) =>
                    this.handleOpenUpdateModal(target, props.vendors.edges)
                  }
                >
                  {i18n.t('Edit Vendor')}
                </MenuItem>
                <MenuItem
                  onClick={(e, data, target) =>
                    this.handleDeleteVendor(target, props.vendors.edges)
                  }
                >
                  {i18n.t('Delete Vendor')}
                </MenuItem>
              </ContextMenu>
            </Page>
          );
        }}
      />
    );
  }
}

export default VendorList;
